import { Product } from '@/pages/pos/types/pos.types';
import { authFetch } from './api.service';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

interface POSProductsResponse {
  success: boolean;
  data: Product[];
}

interface CategoryResponse {
  success: boolean;
  data: Array<{
    id: string;
    name: string;
    label: string;
    icon?: string;
  }>;
}

interface PresentationResponse {
  success: boolean;
  data: Array<{
    id: string;
    name: string;
    description: string;
    price: number;
    cost: number;
    isDefault: boolean;
    sortOrder: number;
  }>;
}

interface SaleItem {
  productId: string;
  cantidad: number;
  precioUnitario: number;
  descuento?: number;
}

interface SaleData {
  items: SaleItem[];
  metodoPago: 'efectivo' | 'tarjeta' | 'yape' | 'aplicativo';
  cliente?: {
    tipo: string;
    nombre?: string;
    documento?: string;
    email?: string;
    telefono?: string;
    fechaNacimiento?: string;
    direccion?: string;
  };
  comprobante?: {
    tipo: 'BOLETA' | 'FACTURA';
  };
  observaciones?: string;
}

interface SaleResponse {
  success: boolean;
  data?: any;
  error?: string;
  message?: string;
}

class POSService {
  private getLocationId(): string {
    try {
      // Obtener la ubicación seleccionada desde el formato usado por AuthContext
      const selectedLocationStr = localStorage.getItem('macartur:lastSelectedLocation');
      if (selectedLocationStr) {
        const selectedLocation = JSON.parse(selectedLocationStr);
        return selectedLocation.id || 'location-1';
      }
      
      // Fallback: obtener usuario y usar su locationId asignado
      const userStr = localStorage.getItem('macartur:user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.locationId || 'location-1';
      }
      
      return 'location-1';
    } catch (error) {
      console.warn('Error getting location ID, using fallback:', error);
      return 'location-1';
    }
  }

  /**
   * Obtener productos para POS con presentaciones activas
   */
  async getProducts(params: {
    search?: string;
    category?: string;
    includeInactive?: boolean;
    isDelivery?: boolean;
  } = {}): Promise<POSProductsResponse> {
    const queryParams = new URLSearchParams();

    if (params.search) queryParams.append('search', params.search);
    if (params.category && params.category !== 'all') queryParams.append('category', params.category);
    if (params.isDelivery) queryParams.append('isDelivery', 'true');
    if (params.includeInactive) queryParams.append('includeInactive', 'true');

    const url = `${API_BASE_URL}/pos/products${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    
    const response = await authFetch(url, {
      method: 'GET',
          });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener productos');
    }

    return await response.json();
  }

  /**
   * Obtener categorías de productos
   */
  async getCategories(): Promise<CategoryResponse> {
    const response = await authFetch(`${API_BASE_URL}/pos/categories`, {
      method: 'GET',
          });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener categorías');
    }

    return await response.json();
  }

  /**
   * Obtener productos complementos
   */
  async getComplements(params: { isDelivery?: boolean } = {}): Promise<POSProductsResponse> {
    const queryParams = new URLSearchParams();
    if (params.isDelivery) queryParams.append('isDelivery', 'true');
    const qs = queryParams.toString();
    const response = await authFetch(`${API_BASE_URL}/pos/complements${qs ? `?${qs}` : ''}`, {
      method: 'GET',
          });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener complementos');
    }

    return await response.json();
  }

  /**
   * Obtener presentaciones de un producto específico
   */
  async getProductPresentations(productId: string): Promise<PresentationResponse> {
    const response = await authFetch(`${API_BASE_URL}/pos/product/${productId}/presentations`, {
      method: 'GET',
          });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener presentaciones');
    }

    return await response.json();
  }

  /**
   * Obtener consideraciones disponibles
   */
  async getConsiderations(): Promise<any> {
    const response = await authFetch(`${API_BASE_URL}/pos/considerations`, {
      method: 'GET',
          });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener consideraciones');
    }

    return await response.json();
  }

  /**
   * Crear nueva venta
   */
  async createSale(saleData: SaleData): Promise<SaleResponse> {
    const locationId = this.getLocationId();
    
    const response = await authFetch(`${API_BASE_URL}/locations/${locationId}/sales`, {
      method: 'POST',
      body: JSON.stringify(saleData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al crear la venta');
    }

    return await response.json();
  }

  /**
   * Buscar cliente por número de documento
   */
  async searchCustomer(document: string): Promise<any> {
    const response = await authFetch(`${API_BASE_URL}/customers/search/${document}`, {
      method: 'GET',
          });

    if (!response.ok) {
      if (response.status === 404) {
        return { success: false, data: null, error: 'Cliente no encontrado' };
      }
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al buscar cliente');
    }

    return await response.json();
  }
}

export const posService = new POSService();